/** Projector mode enlarges the existing player, never reparents/reloads it. */
export class VideoFocus {
  #stage: HTMLElement;
  #controls: HTMLElement;
  #toggle: HTMLButtonElement;
  #link: HTMLAnchorElement;
  #title: HTMLElement;
  #video?: HTMLElement;
  #expanded = false;

  constructor(stage: HTMLElement, viewport: HTMLElement) {
    this.#stage = stage;
    this.#controls = document.createElement('div');
    this.#controls.className = 'video-focus-controls';
    this.#controls.hidden = true;
    this.#title = document.createElement('div');
    this.#title.className = 'video-focus-title';
    const actions = document.createElement('div');
    actions.className = 'video-focus-actions';
    this.#toggle = document.createElement('button');
    this.#toggle.type = 'button';
    this.#toggle.className = 'button button-quiet';
    this.#toggle.addEventListener('click', () => this.expand(!this.#expanded));
    this.#link = document.createElement('a');
    this.#link.className = 'button button-quiet';
    this.#link.textContent = 'Open on YouTube ↗';
    this.#link.target = '_blank';
    this.#link.rel = 'noopener noreferrer';
    actions.append(this.#link, this.#toggle);
    this.#controls.append(this.#title, actions);
    viewport.append(this.#controls);
    viewport.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !this.#expanded) return;
      event.preventDefault();
      event.stopPropagation();
      this.expand(false);
      this.#toggle.focus();
    });
  }

  sync(title: string): void {
    this.#title.textContent = title;
    this.#stage.classList.add('has-video-focus');
    const videos = [...this.#stage.querySelectorAll<HTMLElement>('.slide-video')]
      .filter(figure => !figure.closest('.is-hidden') && figure.querySelector('video, iframe'));
    const video = videos.at(-1);
    if (video === this.#video) return;
    this.clear();
    this.#video = video;
    if (!video) return;
    const link = video.querySelector<HTMLAnchorElement>('.video-external-link');
    this.#link.hidden = !link;
    this.#link.href = link?.href ?? '';
    this.#controls.hidden = false;
    this.expand(true);
  }

  clear(): void {
    this.expand(false);
    this.#video = undefined;
    this.#controls.hidden = true;
  }

  isActive(figure: Element): boolean {
    return figure === this.#video;
  }

  private expand(expanded: boolean): void {
    this.#expanded = expanded;
    this.#title.hidden = !expanded;
    this.#controls.classList.toggle('is-expanded', expanded);
    this.#stage.classList.toggle('is-video-focused', expanded);
    this.#video?.classList.toggle('is-focused-video', expanded);
    for (const ancestor of this.#stage.querySelectorAll('.video-focus-ancestor')) {
      ancestor.classList.remove('video-focus-ancestor');
    }
    if (expanded) {
      for (let node = this.#video?.parentElement; node && node !== this.#stage; node = node.parentElement) {
        node.classList.add('video-focus-ancestor');
      }
    }
    this.#toggle.textContent = expanded ? 'Show slide' : 'Enlarge video';
    this.#toggle.ariaPressed = String(expanded);
    this.#toggle.title = expanded ? 'Show text alongside the video (Escape)' : 'Fill the presentation with the video';
  }
}
